import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import Can from '../../components/Can';
import ConfirmModal from '../../components/ConfirmModal';
import './Overtime.css';

const STATUS_TABS = ['Pending', 'Approved', 'Paid', 'Rejected', 'All'];

const EMPTY = {
  employee_id: '', ot_date: today(), hours: '', rate_per_hr: '', reason: '',
};

function OTStatusPill({ status }) {
  return (
    <span className={`ot-status ot-status-${(status || '').toLowerCase()}`}>{status}</span>
  );
}

function Kpi({ label, value, sub, tone }) {
  return (
    <div className={`ot-kpi ${tone ? 'kpi-' + tone : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

export default function Overtime() {
  const { t: tr } = useTranslation();
  const [status, setStatus] = useState('Pending');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [payDraft, setPayDraft] = useState(null);
  const [rejectDraft, setRejectDraft] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const { data, loading, error, reload } = useApi(
    () => api.overtime.list(status === 'All' ? null : status),
    [status]
  );
  const { data: employees } = useApi(() => api.employees.list(), [], []);
  const { data: banksData } = useApi(() => api.banks.list(), [], { banks: [] });
  const banks = banksData.banks || [];

  const activeEmps = useMemo(
    () => (employees || []).filter(e => Number(e.is_active) === 1),
    [employees]
  );

  const selectedEmp = activeEmps.find(e => String(e.id) === String(form.employee_id));

  // Auto-fill rate when employee is picked
  React.useEffect(() => {
    if (selectedEmp && !form.rate_per_hr) {
      setForm(f => ({ ...f, rate_per_hr: selectedEmp.ot_rate_per_hr || '' }));
    }
  }, [form.employee_id]);

  const computedAmount = useMemo(() => {
    const h = parseFloat(form.hours || 0);
    const r = parseFloat(form.rate_per_hr || 0);
    return (h > 0 && r > 0) ? h * r : 0;
  }, [form.hours, form.rate_per_hr]);

  const handleSave = async () => {
    if (!form.employee_id) { notify('❌ ' + tr('overtime.errors.selectEmployee')); return; }
    if (!form.ot_date) { notify('❌ ' + tr('overtime.errors.pickDate')); return; }
    const h = parseFloat(form.hours || 0);
    const r = parseFloat(form.rate_per_hr || 0);
    if (h <= 0 || h > 24) { notify('❌ ' + tr('overtime.errors.hoursRange')); return; }
    if (r <= 0) { notify('❌ ' + tr('overtime.errors.ratePositive')); return; }

    setSaving(true);
    try {
      await api.overtime.create({
        employee_id: parseInt(form.employee_id),
        ot_date: form.ot_date,
        hours: h,
        rate_per_hr: r,
        reason: form.reason.trim(),
      });
      notify('✅ ' + tr('overtime.toasts.otRecorded', { amount: fmtNum(computedAmount) }));
      setForm(EMPTY); setShowForm(false);
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleApprove = async (id) => {
    try {
      await api.overtime.approve(id);
      notify('✅ ' + tr('common.approved'));
      reload();
    } catch (e) { notify('❌ ' + e.message); }
  };

  const handlePay = async () => {
    try {
      await api.overtime.pay(payDraft.id, {
        mode: payDraft.mode,
        paid_ref: payDraft.paid_ref || '',
      });
      notify('✅ ' + tr('overtime.toasts.paidVia', { amount: payDraft.amount, mode: payDraft.mode }));
      setPayDraft(null);
      reload();
    } catch (e) { notify('❌ ' + e.message); }
  };

  const handleReject = async () => {
    try {
      await api.overtime.reject(rejectDraft.id, rejectDraft.reason);
      notify('✅ ' + tr('common.rejected'));
      setRejectDraft(null);
      reload();
    } catch (e) { notify('❌ ' + e.message); }
  };

  const handleDelete = async () => {
    try {
      await api.overtime.remove(confirmDelete.id);
      notify('✅ ' + tr('overtime.toasts.deleted'));
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirmDelete(null);
  };

  const totals = data?.totals || {};

  return (
    <div className="ot-page">
      <div className="page-header">
        <h2 className="page-title">{tr('overtime.title')}</h2>
        <Can module="overtime" action="create">
          <button className="btn btn-primary btn-sm"
                  onClick={() => { setShowForm(!showForm); setForm(EMPTY); }}>
            {showForm ? `✕ ${tr('common.cancel')}` : `＋ ${tr('overtime.recordOt')}`}
          </button>
        </Can>
      </div>

      {msg && <div className="ot-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {/* KPIs */}
      <div className="ot-kpis">
        <Kpi label={tr('overtime.pendingApproval')}  value={`₹${fmtNum(totals.pending_amt || 0)}`}
             sub={`${totals.pending_hrs || 0} ${tr('overtime.hrsUnit')}`} tone="orange" />
        <Kpi label={tr('overtime.approvedUnpaid')} value={`₹${fmtNum(totals.approved_amt || 0)}`} tone="blue" />
        <Kpi label={tr('overtime.paidThisPeriod')}  value={`₹${fmtNum(totals.paid_amt || 0)}`} tone="green" />
      </div>

      {/* Status tabs */}
      <div className="ot-tabs">
        {STATUS_TABS.map(t => (
          <button key={t} className={`ot-tab ${status === t ? 'active' : ''}`}
                  onClick={() => setStatus(t)}>{tr(`common.${t.toLowerCase()}`)}</button>
        ))}
      </div>

      {/* New OT form */}
      {showForm && (
        <div className="section-card">
          <div className="section-header">{tr('overtime.recordOtHours')}</div>
          <div className="ot-form-grid">
            <div className="form-group">
              <label>{tr('common.employee')} *</label>
              <select value={form.employee_id}
                      onChange={e => setForm({ ...form, employee_id: e.target.value, rate_per_hr: '' })}>
                <option value="">{`-- ${tr('common.select')} --`}</option>
                {activeEmps.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.emp_code} — {e.name} (₹{fmtNum(e.ot_rate_per_hr)}/hr)
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{tr('common.date')} *</label>
              <input type="date" value={form.ot_date}
                     onChange={e => setForm({ ...form, ot_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{tr('overtime.hours')} *</label>
              <input type="number" step="0.25" value={form.hours}
                     onChange={e => setForm({ ...form, hours: e.target.value })}
                     placeholder={tr('overtime.hoursPlaceholder')} />
            </div>
            <div className="form-group">
              <label>{tr('overtime.ratePerHour')}</label>
              <input type="number" step="0.01" value={form.rate_per_hr}
                     onChange={e => setForm({ ...form, rate_per_hr: e.target.value })} />
            </div>
            <div className="form-group ot-wide">
              <label>{tr('common.reason')}</label>
              <input value={form.reason}
                     onChange={e => setForm({ ...form, reason: e.target.value })}
                     placeholder={tr('overtime.reasonPlaceholder')} />
            </div>
          </div>

          {computedAmount > 0 && (
            <div className="ot-amount-preview">
              {form.hours} {tr('overtime.hrsUnit')} × ₹{form.rate_per_hr} = <strong>₹{fmtNum(computedAmount)}</strong>
            </div>
          )}

          <div style={{ padding: '0 12px 12px' }}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? `⏳ ${tr('common.saving')}` : tr('overtime.submitForApproval')}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">
            {status === 'All' ? tr('overtime.allOtEntries') : tr('overtime.statusOtEntries', { status: tr(`common.${status.toLowerCase()}`) })}
            {' '}({data?.rows?.length || 0})
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{tr('common.date')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.employee')}</th>
                <th style={{ textAlign: 'right' }}>{tr('overtime.hours')}</th>
                <th style={{ textAlign: 'right' }}>{tr('common.rate')}</th>
                <th style={{ textAlign: 'right' }}>{tr('common.amount')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.reason')}</th>
                <th style={{ textAlign: 'center' }}>{tr('common.status')}</th>
                <th style={{ textAlign: 'center' }}>{tr('common.actions')}</th>
              </tr>

            </thead>
            <tbody>
              {(!data?.rows || data.rows.length === 0) ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                  {tr('overtime.noStatusEntries', { status: tr(`common.${status.toLowerCase()}`) })}
                </td></tr>
              ) : data.rows.map(r => (
                <tr key={r.id}>
                  <td style={{ textAlign: 'left' }}>{fmtDate(r.ot_date)}</td>
                  <td style={{ textAlign: 'left', minWidth: '150px' }}>
                    <div style={{ fontWeight: 600 }}>{r.employee_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                      {r.emp_code}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmtNum(r.hours)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>
                    ₹{fmtNum(r.rate_per_hr)}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>
                    ₹ {fmtNum(r.amount)}
                  </td>

                  <td style={{ textAlign: 'left', fontSize: 12 }}>
                    {r.reason || '—'}
                    {r.status === 'Rejected' && r.reject_reason && (
                      <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 2 }}>
                        {tr('common.rejected')}: {r.reject_reason}
                      </div>
                    )}
                    {r.status === 'Paid' && r.paid_at && (
                      <div style={{ fontSize: 11, color: 'var(--secondary)', marginTop: 2 }}>
                        {tr('common.paid')} {fmtDate(r.paid_at.split(' ')[0])} {tr('overtime.via')} {r.paid_mode}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <OTStatusPill status={r.status} />
                  </td>
                  <td style={{ textAlign: 'center' }}>

                    <div className="ot-actions">
                      {r.status === 'Pending' && (
                        <>
                          <Can module="overtime" action="approve">
                            <button className="btn btn-sm" style={{ color: 'var(--secondary)' }}
                                    onClick={() => handleApprove(r.id)}>✓ {tr('overtime.approve')}</button>
                          </Can>
                          <Can module="overtime" action="approve">
                            <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                                    onClick={() => setRejectDraft({ id: r.id, reason: '' })}>✕ {tr('overtime.reject')}</button>
                          </Can>
                          <Can module="overtime" action="delete">
                            <button className="btn btn-sm"
                                    onClick={() => setConfirmDelete(r)}>🗑</button>
                          </Can>
                        </>
                      )}
                      {r.status === 'Approved' && (
                        <Can module="overtime" action="approve">
                          <button className="btn btn-primary btn-sm"
                                  onClick={() => setPayDraft({ id: r.id, amount: r.amount, mode: 'Cash', paid_ref: '' })}>
                            💰 {tr('overtime.pay')}
                          </button>
                        </Can>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pay modal */}
      {payDraft && (
        <div className="ot-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setPayDraft(null); }}>
          <div className="ot-modal">
            <h3>{tr('overtime.payOt')} — ₹{fmtNum(payDraft.amount)}</h3>
            <div className="form-group">
              <label>{tr('overtime.paymentMode')}</label>
              <select value={payDraft.mode}
                      onChange={e => setPayDraft({ ...payDraft, mode: e.target.value })}>
                <option value="Cash">💵 {tr('common.cash')}</option>
                {banks.map(b => (
                  <option key={b.id} value={`Bank:${b.id}`}>🏦 {b.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{tr('common.reference')} ({tr('common.optional')})</label>
              <input value={payDraft.paid_ref}
                     onChange={e => setPayDraft({ ...payDraft, paid_ref: e.target.value })}
                     placeholder={tr('overtime.referencePlaceholder')} />
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {tr('overtime.posts')}: <strong>{tr('overtime.drExpenseOvertime')}</strong> → <strong>{tr('overtime.cr')} {payDraft.mode === 'Cash' ? tr('common.cash') : banks.find(b => `Bank:${b.id}` === payDraft.mode)?.name || tr('common.bank')}</strong>
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button className="btn btn-sm" onClick={() => setPayDraft(null)}>{tr('common.cancel')}</button>
              <button className="btn btn-primary btn-sm" onClick={handlePay}>{tr('overtime.confirmPayment')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectDraft && (
        <div className="ot-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setRejectDraft(null); }}>
          <div className="ot-modal">
            <h3>{tr('overtime.rejectOt')}</h3>
            <div className="form-group">
              <label>{tr('common.reason')} *</label>
              <textarea rows={3} value={rejectDraft.reason}
                        onChange={e => setRejectDraft({ ...rejectDraft, reason: e.target.value })}
                        style={{ width: '100%', padding: 8, border: '1px solid var(--border)', borderRadius: 4 }}
                        placeholder={tr('overtime.rejectReasonPlaceholder')} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button className="btn btn-sm" onClick={() => setRejectDraft(null)}>{tr('common.cancel')}</button>
              <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                      disabled={!rejectDraft.reason.trim()} onClick={handleReject}>
                {tr('overtime.confirmReject')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        title={tr('overtime.deleteOtEntryTitle')}
        message={confirmDelete
          ? tr('overtime.deleteOtMessage', { name: confirmDelete.employee_name, date: fmtDate(confirmDelete.ot_date) })
          : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
        confirmLabel={tr('common.delete')}
      />
    </div>
  );
}

